<template>
  <b-modal v-model="visible" :title="isEdit ? msg('Edit Item') : msg('New Catalog Item')" size="lg" hide-footer
           @hidden="onHidden" no-close-on-backdrop centered
           content-class="ci-modal-content" header-class="ci-modal-header" body-class="p-0">

    <validation-observer ref="observer">
      <!-- Section: Basic Info -->
      <div class="ci-section-header">
        <feather-icon icon="PackageIcon" size="18" class="mr-75 text-primary"/>
        <span>{{ msg('Product Information') }}</span>
      </div>
      <div class="ci-section-body">
        <b-row>
          <b-col cols="12">
            <b-form-group :label="msg('Product Name')" label-for="ci-name" class="mb-75">
              <validation-provider #default="{ errors }" :name="msg('Name')" rules="required">
                <b-form-input id="ci-name" v-model="form.name" :state="errors.length ? false : null"
                              :placeholder="msg('e.g. Premium Cleaning Package')"/>
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col cols="12">
            <b-form-group :label="msg('Description')" label-for="ci-desc" class="mb-75">
              <b-form-textarea id="ci-desc" v-model="form.description" rows="2"
                               :placeholder="msg('Describe the product or service...')"/>
            </b-form-group>
          </b-col>
        </b-row>
      </div>

      <!-- Section: Pricing & Stock -->
      <div class="ci-section-header">
        <feather-icon icon="DollarSignIcon" size="18" class="mr-75 text-success"/>
        <span>{{ msg('Pricing & Inventory') }}</span>
      </div>
      <div class="ci-section-body">
        <b-row class="align-items-start">
          <b-col cols="12" md="4">
            <label class="d-block mb-50" for="ci-price">{{ msg('Price') }}</label>
            <validation-provider #default="{ errors }" :name="msg('Price')" rules="required|between:0.01,999999">
              <b-input-group :prepend="currencySymbol" class="ci-price-group">
                <b-form-input id="ci-price" v-model.number="form.price" type="number" step="0.01"
                              :state="errors.length ? false : null" placeholder="0.00"/>
              </b-input-group>
              <div class="ci-field-hint" :class="{ 'text-danger': errors.length }">{{ errors[0] || '&nbsp;' }}</div>
            </validation-provider>
          </b-col>
          <b-col cols="6" md="4">
            <label class="d-block mb-50">{{ msg('Currency') }}</label>
            <v-select v-model="form.currency" :options="['EUR','USD','GBP','CHF']" :clearable="false"
                      class="ci-currency-select"/>
            <div class="ci-field-hint">&nbsp;</div>
          </b-col>
          <b-col cols="6" md="4">
            <label class="d-block mb-50" for="ci-stock">{{ msg('Stock') }}</label>
            <b-form-input id="ci-stock" v-model.number="form.stock" type="number" min="0"
                          :placeholder="msg('Unlimited')"/>
            <div class="ci-field-hint text-muted">{{ msg('Leave empty for unlimited') }}</div>
          </b-col>
        </b-row>
      </div>

      <!-- Section: Availability -->
      <div class="ci-section-header">
        <feather-icon icon="GitBranchIcon" size="18" class="mr-75 text-warning"/>
        <span>{{ msg('Availability') }}</span>
      </div>
      <div class="ci-section-body">
        <b-row>
          <b-col cols="12" md="8">
            <label class="d-block mb-50">{{ msg('Branch Availability') }}</label>
            <div class="ci-toggle-group mb-75">
              <div class="ci-toggle-card" :class="{ 'ci-toggle-card--selected': allBranchesToggle }"
                   @click="allBranchesToggle = true">
                <feather-icon icon="GlobeIcon" size="18" class="ci-toggle-card__icon"/>
                <span class="ci-toggle-card__label">{{ msg('All branches') }}</span>
                <feather-icon v-if="allBranchesToggle" icon="CheckCircleIcon" size="16" class="ci-toggle-card__check"/>
              </div>
              <div class="ci-toggle-card" :class="{ 'ci-toggle-card--selected': !allBranchesToggle }"
                   @click="allBranchesToggle = false">
                <feather-icon icon="GitBranchIcon" size="18" class="ci-toggle-card__icon"/>
                <span class="ci-toggle-card__label">{{ msg('Specific') }}</span>
                <feather-icon v-if="!allBranchesToggle" icon="CheckCircleIcon" size="16" class="ci-toggle-card__check"/>
              </div>
            </div>
            <transition name="fade">
              <v-select v-if="!allBranchesToggle" multiple :dir="isRTL ? 'rtl' : 'ltr'" v-model="form.branchIds"
                        :options="branchOptions" label="name" :reduce="b => b.id" class="w-100 mb-75"
                        :placeholder="msg('Select branches...')"/>
            </transition>
          </b-col>
          <b-col cols="12" md="4">
            <label class="d-block mb-50">{{ msg('Listing Status') }}</label>
            <div class="ci-toggle-group">
              <div class="ci-toggle-card ci-toggle-card--status"
                   :class="form.active ? 'ci-toggle-card--active' : 'ci-toggle-card--inactive'"
                   @click="form.active = !form.active">
                <feather-icon :icon="form.active ? 'EyeIcon' : 'EyeOffIcon'" size="20" class="ci-toggle-card__icon"/>
                <span class="ci-toggle-card__label">{{ form.active ? msg('Active') : msg('Inactive') }}</span>
                <small class="ci-toggle-card__hint">{{ form.active ? msg('Visible in shop') : msg('Hidden from shop') }}</small>
              </div>
            </div>
          </b-col>
        </b-row>
      </div>

      <!-- Section: Images (edit only) -->
      <template v-if="isEdit">
        <div class="ci-section-header">
          <feather-icon icon="ImageIcon" size="18" class="mr-75 text-info"/>
          <span>{{ msg('Product Images') }}</span>
        </div>
        <div class="ci-section-body">
          <div v-if="form.imageUrls && form.imageUrls.length" class="ci-gallery mb-1">
            <div v-for="(url, idx) in form.imageUrls" :key="idx" class="ci-gallery__item">
              <img :src="getUrl(url)" class="ci-gallery__img"/>
              <div class="ci-gallery__overlay" @click="removeImage(idx)">
                <feather-icon icon="TrashIcon" size="16"/>
              </div>
            </div>
          </div>
          <div v-else class="ci-gallery-empty mb-1">
            <feather-icon icon="CameraIcon" size="24" class="text-muted mb-50"/>
            <small class="text-muted">{{ msg('No images uploaded yet') }}</small>
          </div>
          <vue-dropzone ref="dropzone" id="ci-dropzone" :options="dropzoneOptions"
                        @vdropzone-success="onUploadSuccess"/>
        </div>
      </template>

      <!-- Footer Actions -->
      <div class="ci-footer">
        <b-button variant="outline-secondary" class="ci-footer__btn" @click="visible = false">
          {{ msg('Cancel') }}
        </b-button>
        <b-button variant="primary" class="ci-footer__btn ci-footer__btn--primary" :disabled="saving" @click="submit">
          <b-spinner v-if="saving" small class="mr-50"/>
          <feather-icon v-else :icon="isEdit ? 'SaveIcon' : 'PlusCircleIcon'" size="15" class="mr-50"/>
          {{ isEdit ? msg('Save Changes') : msg('Create Item') }}
        </b-button>
      </div>
    </validation-observer>
  </b-modal>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import vue2Dropzone from 'vue2-dropzone'
import 'vue2-dropzone/dist/vue2Dropzone.min.css'

export default {
  name: 'CatalogItemModal',
  components: { vueDropzone: vue2Dropzone },
  props: {
    value: Boolean,
    item: { type: Object, default: null },
  },
  data() {
    return {
      saving: false,
      allBranchesToggle: true,
      form: this.buildForm(this.item),
    }
  },
  computed: {
    ...mapGetters('data', ['getAllBranches']),
    visible: {
      get() { return this.value },
      set(v) { this.$emit('input', v) },
    },
    isEdit() { return this.item && this.item.id },
    branchOptions() { return Object.values(this.getAllBranches) },
    currencySymbol() {
      const map = { EUR: '€', USD: '$', GBP: '£', CHF: 'Fr' }
      return map[this.form.currency] || this.form.currency
    },
    dropzoneOptions() {
      return {
        url: window.config.apiUrl + 'catalog/item/upload/img',
        headers: {
          Authorization: 'Bearer ' + localStorage.Bearer,
          itemId: this.item ? this.item.id : '',
        },
        paramName: 'file',
        maxFilesize: 5,
        acceptedFiles: 'image/*',
        addRemoveLinks: true,
        dictDefaultMessage: '<i class="feather" data-feather="upload-cloud"></i> ' + this.msg('Drop images here or click to upload'),
      }
    },
  },
  watch: {
    item(val) {
      this.form = this.buildForm(val)
      this.allBranchesToggle = !val || !val.branchIds || val.branchIds.length === 0
    },
  },
  methods: {
    ...mapActions('catalog', ['addItem', 'updateItem']),
    buildForm(item) {
      if (!item) return { name: '', description: '', price: null, currency: 'EUR', stock: null, branchIds: [], imageUrls: [], active: true }
      return {
        name: item.name || '',
        description: item.description || '',
        price: item.price || null,
        currency: item.currency || 'EUR',
        stock: item.stock,
        branchIds: item.branchIds ? [...item.branchIds] : [],
        imageUrls: item.imageUrls ? [...item.imageUrls] : [],
        active: item.active !== false,
      }
    },
    removeImage(idx) {
      this.form.imageUrls.splice(idx, 1)
    },
    onUploadSuccess(file, response) {
      if (response && response.objects && response.objects.url) {
        this.form.imageUrls.push(response.objects.url)
      }
      if (this.$refs.dropzone) this.$refs.dropzone.removeFile(file)
    },
    async submit() {
      const valid = await this.$refs.observer.validate()
      if (!valid) return
      this.saving = true
      const payload = { ...this.form }
      if (this.allBranchesToggle) payload.branchIds = []
      try {
        if (this.isEdit) {
          await this.updateItem({ id: this.item.id, item: payload })
        } else {
          await this.addItem(payload)
        }
        this.$emit('saved')
        this.visible = false
      } catch (e) {
        console.error(e)
      } finally {
        this.saving = false
      }
    },
    onHidden() {
      this.form = this.buildForm(null)
      this.allBranchesToggle = true
    },
  },
}
</script>

<style scoped>
/* ── Section headers ── */
.ci-section-header {
  display: flex;
  align-items: center;
  padding: 0.65rem 1.25rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  background: rgba(115, 103, 240, 0.03);
  font-size: 0.875rem;
  font-weight: 600;
  color: #5e5873;
}

.ci-section-body {
  padding: 1rem 1.5rem 0.5rem;
}

/* ── Pricing row alignment ── */
.ci-field-hint {
  font-size: 0.75rem;
  line-height: 1.3;
  margin-top: 4px;
  margin-bottom: 8px;
  min-height: 1em;
}

/* ── Toggle cards ── */
.ci-toggle-group {
  display: flex;
  gap: 8px;
}

.ci-toggle-card {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  padding: 10px 12px;
  border: 2px solid #e4e7ec;
  border-radius: 8px;
  background: #fafafa;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: center;
  min-height: 60px;
}

.ci-toggle-card:hover {
  border-color: #c5c5d2;
  background: #f5f5fa;
}

.ci-toggle-card--selected {
  border-color: #7367f0;
  background: rgba(115, 103, 240, 0.06);
}

.ci-toggle-card--selected:hover {
  border-color: #7367f0;
  background: rgba(115, 103, 240, 0.1);
}

.ci-toggle-card__icon {
  margin-bottom: 2px;
  color: #b9b9c3;
  transition: color 0.2s;
}

.ci-toggle-card--selected .ci-toggle-card__icon {
  color: #7367f0;
}

.ci-toggle-card__label {
  font-size: 0.82rem;
  font-weight: 600;
  color: #6e6b7b;
  transition: color 0.2s;
}

.ci-toggle-card--selected .ci-toggle-card__label {
  color: #7367f0;
}

.ci-toggle-card__check {
  position: absolute;
  top: 5px;
  right: 5px;
  color: #7367f0;
}

.ci-toggle-card__hint {
  font-size: 0.7rem;
  color: #b9b9c3;
  margin-top: 1px;
}

/* ── Status variants ── */
.ci-toggle-card--status {
  min-height: 68px;
}

.ci-toggle-card--active {
  border-color: #28c76f;
  background: rgba(40, 199, 111, 0.06);
}

.ci-toggle-card--active:hover {
  background: rgba(40, 199, 111, 0.1);
}

.ci-toggle-card--active .ci-toggle-card__icon {
  color: #28c76f;
}

.ci-toggle-card--active .ci-toggle-card__label {
  color: #28c76f;
}

.ci-toggle-card--inactive {
  border-color: #e4e7ec;
  background: #fafafa;
}

.ci-toggle-card--inactive .ci-toggle-card__icon {
  color: #ea5455;
}

.ci-toggle-card--inactive .ci-toggle-card__label {
  color: #ea5455;
}

/* ── Image gallery ── */
.ci-gallery {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.ci-gallery__item {
  position: relative;
  width: 90px;
  height: 90px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e4e7ec;
  transition: transform 0.2s;
}

.ci-gallery__item:hover {
  transform: scale(1.04);
}

.ci-gallery__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.ci-gallery__overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(234, 84, 85, 0.6);
  color: #fff;
  opacity: 0;
  transition: opacity 0.2s;
  cursor: pointer;
}

.ci-gallery__item:hover .ci-gallery__overlay {
  opacity: 1;
}

.ci-gallery-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1.25rem;
  border: 2px dashed #e4e7ec;
  border-radius: 8px;
  background: #fafafa;
}

/* ── Footer actions ── */
.ci-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 1rem 1.5rem;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  background: rgba(0, 0, 0, 0.01);
}

.ci-footer__btn {
  border-radius: 6px;
  font-weight: 600;
  padding: 0.5rem 1.25rem;
  transition: box-shadow 0.15s ease, transform 0.1s ease;
}

.ci-footer__btn:hover:not(:disabled) {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.ci-footer__btn--primary {
  padding: 0.55rem 1.5rem;
  font-size: 0.9375rem;
}

/* ── Fade transition ── */
.fade-enter-active, .fade-leave-active { transition: opacity 0.2s; }
.fade-enter, .fade-leave-to { opacity: 0; }
</style>

<style>
/* ── Modal chrome ── */
.ci-modal-header {
  border-bottom: none !important;
  padding-bottom: 0.25rem !important;
}

.ci-modal-header .modal-title {
  font-weight: 700 !important;
  font-size: 1.15rem !important;
}

/* ── Dropzone override ── */
#ci-dropzone.vue-dropzone {
  border: 2px dashed #d8d6de;
  border-radius: 8px;
  background: #fafafa;
  min-height: 100px;
  transition: border-color 0.2s;
}

#ci-dropzone.vue-dropzone:hover {
  border-color: #7367f0;
}

#ci-dropzone .dz-message {
  margin: 1.5em 0;
  color: #b9b9c3;
  font-size: 0.9rem;
}

/* ── Dark mode ── */
.dark-layout .ci-modal-content {
  background-color: #283046;
}

.dark-layout .ci-modal-header {
  background-color: #283046;
  color: #d0d2d6;
}

.dark-layout .ci-section-header {
  background: rgba(115, 103, 240, 0.06);
  border-bottom-color: rgba(255, 255, 255, 0.05);
  color: #b4b7bd;
}

.dark-layout .ci-section-body {
  color: #b4b7bd;
}

.dark-layout .ci-toggle-card {
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.1);
}

.dark-layout .ci-toggle-card:hover {
  background: rgba(255, 255, 255, 0.07);
  border-color: rgba(255, 255, 255, 0.18);
}

.dark-layout .ci-toggle-card--selected {
  background: rgba(115, 103, 240, 0.1);
  border-color: #7367f0;
}

.dark-layout .ci-toggle-card__icon { color: #676d7d; }
.dark-layout .ci-toggle-card__label { color: #b4b7bd; }
.dark-layout .ci-toggle-card__hint { color: #676d7d; }

.dark-layout .ci-toggle-card--active {
  background: rgba(40, 199, 111, 0.1);
  border-color: rgba(40, 199, 111, 0.4);
}

.dark-layout .ci-toggle-card--inactive {
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.1);
}

.dark-layout .ci-gallery__item {
  border-color: rgba(255, 255, 255, 0.1);
}

.dark-layout .ci-gallery-empty {
  border-color: rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.03);
}

.dark-layout .ci-footer {
  border-top-color: rgba(255, 255, 255, 0.05);
  background: rgba(255, 255, 255, 0.02);
}

.dark-layout #ci-dropzone.vue-dropzone {
  border-color: rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.03);
}

.dark-layout #ci-dropzone.vue-dropzone:hover {
  border-color: #7367f0;
}
</style>
